import React, { useEffect, useState } from 'react';
import { Formik } from 'formik';
import { Button, Col, Collapse, Divider, Row, Typography } from 'antd';
import { Form, FormItem, Input, ResetButton, Select, SubmitButton } from 'formik-antd';
import { useIntl } from 'react-intl';
import PropTypes from 'prop-types';
import isEmpty from 'lodash/isEmpty';
import { useSelector } from 'react-redux';
import IntlMessages from '../../../../layout/components/lang/IntlMessages';
import Yup from '../../../../vendor/yup';
import { PUSH_NOTIFICATIONS_CONTENT_TYPE, mkValidationSchema } from './FormCreatePushNotification.const';
import { Permissions } from '../../../../const/permissions';
import { fieldTypeToValidationRule, TYPE_IMAGE, TYPE_OBJECT } from '../../../Template builder/const/templates';
import { PrivateComponent } from '../../../../components/HasRights/HasRights';
import getTemplateOptions from '../../../../lib/getTemplateOptions';
import makeSelectFilterOption from '../../../../lib/makeSelectFilterOption';
import getTestPushNotificationData from '../../../../lib/getTestPushNotificationData';
import handleResponse from '../../../../lib/handleResponse';
import useModifiedDynamicDataErrors from '../../../../app/hooks/useModifiedDynamicDataErrors';
import modifyDefaultValue from '../../../Template builder/utils/modifyDefaultValue';
import mkMainAndOptionalTemplateFields from '../../../Template builder/utils/mkMainAndOptionalTemplateFields';
import isFieldRequired from '../../../Content/utils/isFieldRequired';
import mkTemplateDynamicFormData from '../../../Content/utils/mkTemplateDynamicFormData';
import makeTemplatesQueryParams from '../../../../lib/makeTemplatesQueryParams';
import Spinner from '../../../../components/Spinner';
import TitleWithAutomaticGeneration from '../../../../components/TitleWithAutomaticGeneration';
import DynamicFormDataFields from '../../../Content/components/DynamicFormDataFields';
import TemplateVisualization from '../../../Template builder/components/TemplateVisualization';
import { useGetTemplatesQuery } from '../../../Template builder/api/templatesApiSlice';
import { useGetTemplatesVisualizationsQuery } from '../../../Template builder/api/templatesVisualizationsApiSlice';
import { useSendTestPushNotificationMutation } from '../../api/pushNotificationsApiSlice';



const { Title } = Typography;
const { Panel } = Collapse;

let validation = {};

const FormCreatePushNotification = ({
  initialErrors,
  onSubmit,
  isSubmitting,
}) => {
  const intl = useIntl();
  const language = useSelector(({ customise: { language } }) => language);
  const [ currentTemplate, setCurrentTemplate ] = useState(null);
  const [ sendTestButtonDisabled, setSendTestButtonDisabled ] = useState(false);

  const {
    data: { data: visualizations } = { data: [] },
    isFetching: isLoadingVisualizations,
  } = useGetTemplatesVisualizationsQuery({
    queryParams: `search=template_id:${currentTemplate}`,
  }, { skip: !currentTemplate });

  const [ sendTestPushNotification, { isLoading: isSendingTestPushNotification } ] = useSendTestPushNotificationMutation();

  const sendTestPush = (values) => {
    const formData = getTestPushNotificationData(values);

    sendTestPushNotification({ formData })
      .unwrap()
      .then(() => handleResponse('success', intl, 'push-notifications-send-test-success', [
        () => setSendTestButtonDisabled(true),
        () => setTimeout(() => setSendTestButtonDisabled(false), 10000) ]))
      .catch((error) => handleResponse('error', intl, 'push-notifications-send-test-error', [], error));
  };

  if (!isEmpty(initialErrors)) {
    window.scrollTo(0, 0);
  }

  const { data: templates = { data: [] }, isFetching: isTemplatesFetching } = useGetTemplatesQuery({
    queryParams: makeTemplatesQueryParams('status', PUSH_NOTIFICATIONS_CONTENT_TYPE),
  });

  const template = templates?.data?.find((template) => template.id === currentTemplate);
  const modifiedErrors = useModifiedDynamicDataErrors(currentTemplate, template, initialErrors, language);
  const { mainFields, optionalFields } = mkMainAndOptionalTemplateFields(template?.fields?.data);

  useEffect(() => {
    validation = mkValidationSchema();
  }, []);

  const applyTemplate = async (value, setFieldValue) => {
    setCurrentTemplate(value);
    await setFieldValue('template_id', value);

    if (!value) {
      setFieldValue('data', {});
      return;
    }

    const data = {};
    const dataSchema = {};
    const template = templates?.data?.find((template) => template.id === value);

    template?.fields?.data.filter((field) => field.type !== TYPE_OBJECT)
      .forEach((field) => {
        if (isFieldRequired(field?.validations?.data || [])) {
          dataSchema[field.full_name] = Yup.object().shape({
            template_field_id: Yup.number().required(),
            ...fieldTypeToValidationRule[field.type],
          });
        }

        data[field.full_name] = {
          value: field.type === TYPE_IMAGE ? [] : modifyDefaultValue(field),
          template_field_id: field.id,
        };
      });

    setFieldValue('data', data);

    validation = mkValidationSchema({
      data: Yup.object().shape(dataSchema),
    });
  };

  const initialValues = {
    template_id: null,
    template_field_id: null,
    data: {},
  };

  return (
    <Formik
      enableReinitialize
      isSubmitting
      initialErrors={modifiedErrors}
      initialValues={initialValues}
      validationSchema={validation}
      onSubmit={async (values, { resetForm }) => {
        onSubmit(mkTemplateDynamicFormData(values, language, template?.fields?.data), resetForm);
      }}
    >
      {({ isValid, values, setFieldValue, resetForm }) => {
        return (
          <Form layout="vertical" >
            <Spinner spinning={isSubmitting}>
              <Row gutter={16}>
                <Col md={12} xs={24}>
                  <Row gutter={16}>
                    <Col span={24}>
                      <FormItem
                        label={intl.formatMessage({ id: 'ui-general-template' })}
                        name='template_id'
                        required
                      >
                        <Select
                          loading={isTemplatesFetching}
                          allowClear
                          showSearch
                          name="template_id"
                          placeholder={intl.formatMessage({ id: 'ui-general-choose-template' })}
                          options={getTemplateOptions(templates.data)}
                          onChange={async (value) => {
                            await applyTemplate(value, setFieldValue);
                          }}
                          filterOption={(input, option) => makeSelectFilterOption(option.label, input)}
                        />
                      </FormItem>
                    </Col>

                    <Col span={24}>
                      <Collapse accordion className='hp-mb-24'>
                        <Panel
                          key="additional-info"
                          header={
                            <Title className='collapse-item-title' level={5}>
                              {intl.formatMessage({ id: 'ui-general-optional-data' })}
                            </Title>
                          }
                        >
                          <Row gutter={16}>
                            <Col span={24}>
                              <TitleWithAutomaticGeneration useAutomaticGeneration={false} />
                            </Col>
                          </Row>
                        </Panel>
                      </Collapse>
                    </Col>

                    {values?.template_id && (
                      <Col span={24}>
                        <Divider />
                        <Title className="hp-mb-24" level={4}><IntlMessages id="ui-general-template-data" /></Title>
                        <Row className="hp-mb-24">
                          <Col span={24}>
                            <DynamicFormDataFields
                              setFieldValue={setFieldValue}
                              fields={mainFields}
                              values={values}
                              span={24}
                            />
                            {!!optionalFields.length && (
                              <Collapse>
                                <Panel
                                  key="1"
                                  header={
                                    <Title level={5} className='collapse-item-title'>
                                      <IntlMessages id="ui-general-additional-parameters" />
                                    </Title>
                                  }
                                >
                                  <DynamicFormDataFields
                                    setFieldValue={setFieldValue}
                                    fields={optionalFields}
                                    values={values}
                                    span={24}
                                  />
                                </Panel>
                              </Collapse>
                            )}
                          </Col>
                        </Row>
                      </Col>
                    )}
                  </Row>
                </Col>

                <Col md={12} xs={24}>
                  <PrivateComponent allowedPermissions={[ Permissions.CONSTRUCTORS.TEMPLATES.VISUALIZATIONS.VIEW_PUSH_NOTIFICATION_VISUALIZATIONS ]}>
                    <TemplateVisualization
                      isLoading={isLoadingVisualizations}
                      currentValues={values}
                      visualizations={visualizations}
                      dynamicData={template?.fields?.data}
                    />
                  </PrivateComponent>
                  <PrivateComponent allowedPermissions={[ Permissions.OTHER.NOTIFICATIONS.PUSH.TEST ]}>
                    <div className='send-test-push-notification-block hp-mb-24'>
                      <FormItem
                        name='user_token'
                        required
                        label={<IntlMessages id="push-notifications-device-token" />}
                      >
                        <Input
                          name="user_token"
                          placeholder={intl.formatMessage({ id: 'push-notifications-device-token' })}
                        />
                      </FormItem>

                      <Button
                        className='w-full'
                        type="primary"
                        onClick={() => sendTestPush(values)}
                        loading={isSendingTestPushNotification || sendTestButtonDisabled}
                        disabled={!values.user_token || !currentTemplate || sendTestButtonDisabled}
                      >
                        <IntlMessages id="push-notifications-send-test" />
                      </Button>

                      <div className='disabled-hint-text hp-mt-8'>
                        <IntlMessages id="push-notifications-test-publish-note" />
                      </div>
                    </div>
                  </PrivateComponent>
                </Col>
              </Row>

              <Row gutter={[ 16, 16 ]} className='hp-mt-16' justify='end'>
                <Col>
                  <ResetButton
                    disabled={false}
                    onClick={() => {
                      setCurrentTemplate(null);
                      resetForm(initialValues);
                    }}
                  >
                    <IntlMessages id='ui-general-reset' />
                  </ResetButton>
                </Col>

                <Col>
                  <SubmitButton
                    type="primary"
                    loading={isSubmitting}
                    disabled={!isValid}
                  >
                    <IntlMessages id="ui-general-save" />
                  </SubmitButton>
                </Col>
              </Row>
            </Spinner>
          </Form>
        );
      }}
    </Formik>
  );
};

FormCreatePushNotification.propTypes = {
  initialErrors: PropTypes.object.isRequired,
  onSubmit: PropTypes.func.isRequired,
  isSubmitting: PropTypes.bool.isRequired,
};


export default FormCreatePushNotification;
